import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Bell, BellOff, Loader2, Send, Lock, Pin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { Avatar } from "./Media";

const ago = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const useDec = (encKey, ct, iv) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    let live = true;
    if (!encKey) return;
    C.decryptText(encKey, ct, iv).then((p) => live && setT(p)).catch(() => live && setT(""));
    return () => { live = false; };
  }, [encKey, ct, iv]);
  return t;
};

const Composer = ({ encKey, accent, onSend, placeholder, autoFocus, initial = "", onCancel, testid }) => {
  const [txt, setTxt] = useState(initial);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const v = txt.trim();
    if (!v || !encKey) return;
    setBusy(true);
    try { const e = await C.encryptText(encKey, v); await onSend(e.ctB64, e.ivB64); setTxt(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send, please try again"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2 mt-2" data-testid={testid}>
      <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={1} autoFocus={autoFocus} placeholder={placeholder} maxLength={4000}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } if (e.key === "Escape") onCancel?.(); }}
        data-testid={`${testid}-input`} className="flex-1 bg-[#0A0A0A] border border-white/10 focus:border-white/30 px-3 py-2 text-white font-body text-sm outline-none resize-none" />
      {onCancel && <button onClick={onCancel} data-testid={`${testid}-cancel`} className="font-body text-xs text-zinc-500 hover:text-white px-1 py-2">Cancel</button>}
      <button onClick={send} disabled={busy || !txt.trim() || !encKey} aria-label="Send" data-testid={`${testid}-send`}
        className="p-2 disabled:opacity-30 transition-opacity" style={{ color: accent }}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
    </div>
  );
};

const Comment = ({ c, ctx, replies = [], children }) => {
  const { encKey, accent, userId, isOwner, bid, reload, iid, pinnedId, pin } = ctx;
  const pinned = !c.parent_id && c.id === pinnedId;
  const text = useDec(encKey, c.ct, c.iv);
  const [mode, setMode] = useState(null);
  const mine = c.author_id === userId;
  const del = async () => {
    if (!window.confirm(replies.length ? "Delete this comment and its replies?" : "Delete this comment?")) return;
    try { await api.delete(`/beacon/${bid}/comments/${c.id}`); reload(); } catch { toast.error("Couldn't delete"); }
  };
  const edit = async (ct, iv) => { await api.patch(`/beacon/${bid}/comments/${c.id}`, { ct, iv }); setMode(null); reload(); };
  const reply = async (ct, iv) => { await api.post(`/beacon/${bid}/items/${iid}/comments`, { ct, iv, parent_id: c.id }); setMode(null); reload(); };
  return (
    <div className={`flex gap-2.5 py-2 ${pinned ? "px-2 -mx-2 rounded-sm" : ""}`} style={pinned ? { background: accent + "12" } : undefined} data-testid={`beacon-comment-${c.id}`}>
      <Avatar name={c.author?.name} src={c.author?.avatar} iv={c.author?.avatar_iv} size={c.parent_id ? 22 : 26} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 font-body text-xs">
          <span className="text-white font-medium">{c.author?.name || "Member"}</span>
          {c.is_creator && <span className="text-[9px] uppercase tracking-wider" style={{ color: accent }}>Creator</span>}
          <span className="text-zinc-500">{ago(c.created_at)}{c.edited_at && " · edited"}</span>
          {pinned && <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider" style={{ color: accent }} data-testid={`beacon-comment-pinned-${c.id}`}><Pin size={11} /> Pinned</span>}
        </div>
        {mode === "edit" && text !== null
          ? <Composer encKey={encKey} accent={accent} initial={text} autoFocus onSend={edit} onCancel={() => setMode(null)} testid={`beacon-comment-edit-${c.id}`} />
          : <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap break-words mt-0.5" data-testid={`beacon-comment-text-${c.id}`}>{text ?? "·····"}</p>}
        <div className="flex gap-3 mt-1 font-body text-xs text-zinc-400">
          {!c.parent_id && <button onClick={() => setMode(mode === "reply" ? null : "reply")} data-testid={`beacon-comment-reply-${c.id}`} className="hover:text-white">Reply</button>}
          {mine && <button onClick={() => setMode("edit")} data-testid={`beacon-comment-edit-btn-${c.id}`} className="hover:text-white">Edit</button>}
          {!c.parent_id && isOwner && <button onClick={() => pin(pinned ? null : c.id)} data-testid={`beacon-comment-pin-${c.id}`} className="hover:text-white">{pinned ? "Unpin" : "Pin"}</button>}
          {(mine || isOwner) && <button onClick={del} data-testid={`beacon-comment-delete-${c.id}`} className="hover:text-red-400">Delete</button>}
        </div>
        {children}
        {mode === "reply" && <Composer encKey={encKey} accent={accent} autoFocus placeholder="Write a reply…" onSend={reply} onCancel={() => setMode(null)} testid={`beacon-reply-box-${c.id}`} />}
      </div>
    </div>
  );
};

const Thread = ({ bid, iid, encKey, accent, userId, onCount }) => {
  const [data, setData] = useState(null);
  const reload = async () => {
    try { const { data: d } = await api.get(`/beacon/${bid}/items/${iid}/comments`); setData(d); onCount(d.comments.length); }
    catch { setData({ comments: [], error: true }); }
  };
  useEffect(() => { reload(); }, [bid, iid]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleMute = async () => {
    try { await api.put(`/beacon/${bid}/comment-alerts`, { muted: !data.muted }); setData((d) => ({ ...d, muted: !d.muted })); toast.success(data.muted ? "Comment alerts on for this Beacon" : "Comment alerts muted for this Beacon"); }
    catch { toast.error("Couldn't change alerts"); }
  };
  if (!data) return <div className="py-3 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-600" /></div>;
  const top = data.comments.filter((c) => !c.parent_id).sort((a, b) => (b.id === data.pinned_id) - (a.id === data.pinned_id));
  const pin = async (cid) => {
    try { await api.put(`/beacon/${bid}/items/${iid}/pin`, { comment_id: cid }); setData((d) => ({ ...d, pinned_id: cid })); toast.success(cid ? "Pinned to the top" : "Unpinned"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pin"); }
  };
  const ctx = { encKey, accent, userId, isOwner: data.is_owner, bid, iid, reload, pinnedId: data.pinned_id, pin };
  return (
    <div className="mt-2 pl-3 border-l-2" style={{ borderColor: accent + "44" }} data-testid={`beacon-comments-thread-${iid}`}>
      <div className="flex items-center justify-between">
        <span className="font-body text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Lock size={10} /> Private to subscribers</span>
        <button onClick={toggleMute} title={data.muted ? "Alerts muted: tap to turn on" : "Mute comment alerts for this Beacon"} data-testid={`beacon-comments-mute-${iid}`} className="text-zinc-500 hover:text-white p-1">
          {data.muted ? <BellOff size={13} /> : <Bell size={13} />}
        </button>
      </div>
      {top.map((c) => {
        const replies = data.comments.filter((r) => r.parent_id === c.id);
        return (
          <Comment key={c.id} c={c} ctx={ctx} replies={replies}>
            {replies.length > 0 && <div className="mt-1 pl-1 border-l border-white/10">{replies.map((r) => <div key={r.id} className="pl-2.5"><Comment c={r} ctx={ctx} /></div>)}</div>}
          </Comment>
        );
      })}
      {!encKey
        ? <p className="font-body text-xs text-zinc-500 italic py-2" data-testid="beacon-comments-preparing">Your access is being prepared, you'll be able to comment shortly.</p>
        : <Composer encKey={encKey} accent={accent} placeholder={top.length ? "Add a comment…" : "Be the first to comment…"} testid={`beacon-comment-box-${iid}`}
            onSend={async (ct, iv) => { await api.post(`/beacon/${bid}/items/${iid}/comments`, { ct, iv }); reload(); }} />}
    </div>
  );
};

// One gentle pulse on the first comment icon a subscriber ever sees (per device).
let pulseClaimed = null;
const claimPulse = (id) => {
  if (pulseClaimed) return pulseClaimed === id;
  if (localStorage.getItem("ss_comment_pulse_done")) return false;
  pulseClaimed = id;
  localStorage.setItem("ss_comment_pulse_done", "1");
  return true;
};

// Compact comment icon + count under a lesson; tap to open the encrypted thread.
export const BeaconComments = ({ bid, item, encKey, accent, userId, defaultOpen = false, canPulse = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [count, setCount] = useState(item.comment_count || 0);
  const [pulse, setPulse] = useState(() => canPulse && claimPulse(item.id));
  useEffect(() => { if (pulse) { const t = setTimeout(() => setPulse(false), 4500); return () => clearTimeout(t); } }, [pulse]);
  const ref = useRef(null);
  useEffect(() => { if (defaultOpen) setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 400); }, [defaultOpen]);
  return (
    <div className="mt-2" ref={ref} data-testid={`beacon-comments-${item.id}`}>
      <button onClick={() => { setOpen((o) => !o); setPulse(false); }} aria-expanded={open} aria-label="Comments" data-testid={`beacon-comments-toggle-${item.id}`}
        className={`inline-flex items-center gap-1.5 font-body text-xs text-zinc-500 hover:text-white transition-colors rounded-full ${pulse ? "animate-pulse ring-2 px-1.5 py-0.5" : ""}`} style={pulse ? { color: accent, "--tw-ring-color": accent } : open ? { color: accent } : undefined}>
        <MessageCircle size={14} />{count > 0 && <span data-testid={`beacon-comments-count-${item.id}`}>{count}</span>}
        {pulse && <span className="font-body text-[11px]" data-testid="beacon-comments-pulse-hint">Join the conversation</span>}
      </button>
      {open && <Thread bid={bid} iid={item.id} encKey={encKey} accent={accent} userId={userId} onCount={setCount} />}
    </div>
  );
};
